import { ApplicationController } from '@aha-app/mvc';
import type DocumentWithPresence from '../DocumentWithPresence';
import { type FetchUserInfo, type SelectionMap } from '../DocumentWithPresence';

interface UserIDMap {
  [key: string]: { displayName: string; loading: boolean };
}

export interface SelectionsControllerState {
  // Information about selections for each collaborator.
  selections: SelectionMap;
  // Map of userIds to user information.
  users: UserIDMap;
}

export const defaultSelectionsControllerState: SelectionsControllerState = {
  selections: {},
  users: {},
};

class SelectionsController extends ApplicationController<SelectionsControllerState> {
  static initialState = defaultSelectionsControllerState;

  fetchUserInfo: FetchUserInfo;

  async initialize({
    collaborativeDocument,
    fetchUserInfo,
  }: {
    collaborativeDocument: DocumentWithPresence;
    fetchUserInfo?: FetchUserInfo;
  }) {
    if (fetchUserInfo) {
      this.fetchUserInfo = fetchUserInfo;
    }

    this.state.selections = collaborativeDocument.selections;
    // Force the colalborative document to update our proxied version of the
    // selections so that redraws are forced.
    collaborativeDocument.selections = this.state.selections;
  }

  // Return the display name for a user based on their userId, potentially
  // doing an async lookup to find it.
  getDisplayName(userId: string) {
    const { users } = this.state;

    if (Object.hasOwn(users, userId)) {
      const userState = users[userId];

      if (userState.loading) {
        return '';
      } else {
        return userState.displayName;
      }
    }

    users[userId] = { displayName: '', loading: true };
    void this.fetchUserInfo({ userId }).then((userInfo) => {
      // This call may arrive after the component has been unmounted, so
      // any code here should be safe to execute then.
      users[userId] = { displayName: userInfo.displayName, loading: false };
    });

    return '';
  }
}

export default SelectionsController;
